import { Box, Button, Stack } from '@mui/material';
import { AxiosError } from 'axios';
import { useRootContext } from 'components/RootContainer';
import { FC } from 'react';
import { useMutation } from 'react-query';
import { useNavigate } from 'react-router-dom';
import { REPORT_PATH } from '../../../../../../@constants/Navigation/AppPaths/RootPaths';
import EmptyContent from '../../../../../../components/EmptyContent';
import ModalLoading from '../../../../../../components/ModalLoading';
import { ArrowLeft2Icon } from '../../../../../../components/SvgIcons/ArrowLeft2Icon';
import { Bookmark2Icon } from '../../../../../../components/SvgIcons/Bookmark2Icon';
import { useFeedback } from '../../../../../../hooks/useFeedback';
import { ExportErrorFeedback } from '../../../../helpers/ExportErrorFeedback';
import { ForecastByRoomTypeReportRequestModel } from './api/models/ForecastByRoomTypeReportRequestModel';
import { FilterFormProvider } from './components/FilterForm/FilterFormProvider';
import { getDefaultValues } from './components/FilterForm/defaultValues';
import { dataFormToRequest } from './utils/dataFormToRequest';

const ForecastByRoomTypeReportPage: FC = () => {
  const {
    globalSettings,
    HotelApi: {
      report: {
        reservationReport: { forecastByRoomType },
      },
    },
  } = useRootContext();

  const { onError, closeSnackbar } = useFeedback();

  const exportPDF = useMutation({
    mutationKey: ['exportPDF'],
    mutationFn: (payload: ForecastByRoomTypeReportRequestModel) => {
      return forecastByRoomType.exportPDF(payload);
    },
    onError: (error: AxiosError, variables) => {
      const content = (
        <ExportErrorFeedback
          status={error.response?.status}
          message={(error as { message?: string }).message}
          onExportCsv={() => exportPDF.mutate({ ...variables, file_type: 'csv' })}
          onClose={closeSnackbar}
          recommendedFormat="csv"
        />
      );

      onError({
        snackbar: {
          title: 'Error exporting report',
          message: content,
        },
        options: { persist: false },
        autoHideDuration: 5000,
      });
    },
  });

  const navigate = useNavigate();

  const handleBackClick = () => {
    navigate(REPORT_PATH);
  };

  return (
    <>
      <Box
        sx={{
          position: 'relative',
          display: 'flex',
          flexDirection: 'row',
          height: '100%',
        }}
      >
        <Box
          sx={{
            width: '24rem',
            height: '100%',
            display: 'flex',
            flexDirection: 'column',
            backgroundColor: 'colors.gray.o10',
          }}
        >
          <Button
            startIcon={<ArrowLeft2Icon />}
            sx={{
              fontSize: '1rem',
              fontWeight: 'fontWeightMedium',
              justifyContent: 'flex-start',
              m: '1rem',
            }}
            onClick={handleBackClick}
          >
            Back
          </Button>
          <FilterFormProvider
            defaultValues={getDefaultValues(globalSettings!)}
            onSubmit={data => {
              return exportPDF.mutate(dataFormToRequest(data));
            }}
          />
        </Box>
        <Stack
          position="relative"
          flexGrow={1}
          height="100%"
          sx={{
            p: 2,
          }}
        >
          <Box sx={{ flexGrow: 1, height: '100%', position: 'relative' }}>
            <ModalLoading open={exportPDF.isLoading} title="Exporting..." />
            <EmptyContent
              IconProps={<Bookmark2Icon />}
              title="Reports"
              subTitle="Select date and generate report"
            />
          </Box>
        </Stack>
      </Box>
    </>
  );
};

export default ForecastByRoomTypeReportPage;
